import React, { useMemo, useState } from 'react'
import { Check, ChevronDown } from '../../assets/icons'
import { Button } from '../ui/button'
import useIsMobile from '../../hooks/useIsMobile'
import {
	Checkbox,
	EmptyMessage,
	ErrorText,
	ListContainer,
	ModalActions,
	OptionAddress,
	OptionInfo,
	OptionName,
	OptionRow,
	Section,
	SectionDivider,
	SectionTitle,
	SelectButton,
	SelectLabel,
	SelectWrapper,
	TriggerText
} from './styled'
import Modal from '../ui/modal'
import colors from '../../styles/colors'

const CLINIC_GROUP = 'clinicas'
const CENTER_GROUP = 'centros'
const OTHER_GROUP = 'otros'

const getGroupKey = option => {
	const label = String(option?.label ?? '').trim().toLowerCase()
	if (label.startsWith('centro')) return CENTER_GROUP
	if (label.startsWith('clínica') || label.startsWith('clinica')) return CLINIC_GROUP
	return OTHER_GROUP
}

const GROUP_ORDER = [CLINIC_GROUP, CENTER_GROUP, OTHER_GROUP]

const GROUP_TITLES = {
	[CLINIC_GROUP]: 'Clínicas',
	[CENTER_GROUP]: 'Centros médicos',
	[OTHER_GROUP]: 'Otros'
}

const normalizeToArray = value => {
	if (Array.isArray(value)) return value.filter(v => v !== null && v !== undefined && v !== '')
	if (value === null || value === undefined || value === '') return []
	return [value]
}

const buildTriggerText = (selectedValues, options) => {
	if (!selectedValues.length) return ''
	const selectedOptions = options.filter(opt => selectedValues.includes(opt.value))
	if (selectedOptions.length === 1) return selectedOptions[0].label
	if (selectedOptions.length === 2) {
		return selectedOptions.map(opt => opt.label).join(', ')
	}
	return `${selectedOptions[0]?.label ?? ''} +${selectedOptions.length - 1}`
}

const ClinicSelect = ({
	label = 'Clínica / Centro médico',
	name,
	value,
	onChange,
	options = [],
	error,
	disabled = false,
	placeholder = 'Seleccioná una clínica',
	isMultiSelect = false,
	title = '¿Dónde querés atenderte?',
	confirmLabel = 'Continuar',
	cancelLabel
}) => {
	const isMobile = useIsMobile()
	const [isOpen, setIsOpen] = useState(false)
	const [draft, setDraft] = useState([])

	const normalizedValue = useMemo(() => normalizeToArray(value), [value])

	const groupedOptions = useMemo(() => {
		const groups = { [CLINIC_GROUP]: [], [CENTER_GROUP]: [], [OTHER_GROUP]: [] }
		options.forEach(option => {
			groups[getGroupKey(option)].push(option)
		})
		return GROUP_ORDER
			.map(key => ({ key, title: GROUP_TITLES[key], items: groups[key] }))
			.filter(section => section.items.length > 0)
	}, [options])

	const triggerText = buildTriggerText(normalizedValue, options)

	const openModal = () => {
		if (disabled) return
		setDraft(normalizedValue)
		setIsOpen(true)
	}

	const closeModal = () => setIsOpen(false)

	const toggleOption = optionValue => {
		if (isMultiSelect) {
			setDraft(current =>
				current.includes(optionValue)
					? current.filter(v => v !== optionValue)
					: [...current, optionValue]
			)
			return
		}
		setDraft(current => (current[0] === optionValue ? [] : [optionValue]))
	}

	const handleConfirm = () => {
		if (isMultiSelect) {
			onChange && onChange(draft)
		} else {
			onChange && onChange(draft[0] ?? null)
		}
		closeModal()
	}

	const handleCancel = () => {
		setDraft(normalizedValue)
		closeModal()
	}

	const isEmpty = normalizedValue.length === 0
	const confirmDisabled = draft.length === 0

	const componentContainerProps = {
		$alignItems: 'stretch',
		$padding: '72px 24px 24px 24px',
		$paddingDesktop: '56px 32px 24px 32px',
		$gap: '24px',
		$height: isMobile ? '100%' : 'auto',
		$maxHeight: isMobile ? 'none' : 'calc(100dvh - 60px)'
	}

	return (
		<>
			<SelectWrapper>
				{label && <SelectLabel htmlFor={name}>{label}</SelectLabel>}
				<SelectButton
					id={name}
					type="button"
					disabled={disabled}
					$isEmpty={isEmpty}
					$error={!!error}
					onClick={openModal}
				>
					<TriggerText>{isEmpty ? placeholder : triggerText}</TriggerText>
					<ChevronDown width={12.29} height={7.14} />
				</SelectButton>
				{error && <ErrorText>{error}</ErrorText>}
			</SelectWrapper>

			{isOpen && (
				<Modal
					onClose={handleCancel}
					widthDesktop="480px"
					withComponentContainer
					isFullHeight={isMobile}
					withMainSection
					mainSectionProps={{ $grow: true, $gap: '24px' }}
					componentContainerProps={componentContainerProps}
					title={title}
					buttons={
						<ModalActions>
							<Button
								label={confirmLabel}
								onClick={handleConfirm}
								size={isMobile ? 'mobile' : 'large'}
								disabled={confirmDisabled}
							/>
							{cancelLabel && (
								<Button
									label={cancelLabel}
									onClick={handleCancel}
									type="button"
									size="text"
									$variant="tertiary"
									alignSelf="center"
									$mt="0"
									$showBorder={false}
								/>
							)}
						</ModalActions>
					}
				>
					<ListContainer>
						{groupedOptions.length === 0 ? (
							<EmptyMessage>No hay opciones disponibles</EmptyMessage>
						) : (
							groupedOptions.map((section, sectionIndex) => (
								<React.Fragment key={section.key}>
									{sectionIndex > 0 && <SectionDivider />}
									<Section>
										<SectionTitle>{section.title}</SectionTitle>
										{section.items.map(option => {
											const isSelected = draft.includes(option.value)
											return (
												<OptionRow
													key={option.value}
													$selected={isSelected}
													$hasCheckbox={isMultiSelect}
													onClick={() => toggleOption(option.value)}
												>
													{isMultiSelect && (
														<Checkbox $checked={isSelected}>
															{isSelected && <Check color={colors.brand.white} size={14} />}
														</Checkbox>
													)}
													<OptionInfo>
														<OptionName>{option.label}</OptionName>
														{option.direccion && (
															<OptionAddress>{option.direccion}</OptionAddress>
														)}
													</OptionInfo>
												</OptionRow>
											)
										})}
									</Section>
								</React.Fragment>
							))
						)}
					</ListContainer>
				</Modal>
			)}
		</>
	)
}

export default ClinicSelect
